/**
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Polyfills are no longer needed with Vite's modern browser targeting.
// If IE11 support is still required, add specific polyfills via vite.config.ts.

import React from 'react';
import { createRoot } from 'react-dom/client';
import * as log from 'loglevel';
import { last } from 'lodash';
import FontFaceObserver from 'fontfaceobserver';
import setupLogger from 'portal-utils/logger';
import {
  loginUser,
  impersonateRedirect,
  getUserEmail,
  getUserIdp,
  getImpersonatedDisplayName,
} from './auth';
import { appInsights } from './ApplicationInsights';
import env from './utils/getEnv';
import * as serviceWorker from './serviceWorker';
import configureStore from './store/configureStore';
import './index.scss';
// Initialize languages
import './locales/i18n';
import './utils/momentLocales';
import { isInteractionRequiredError } from './api/axiosInstance';
import { performAutoLogout } from './utils/sessionTimeout';
import ConnectedApp from './ConnectedApp';
import { getLoggedUserInfo } from './utils/analytics';

// setup Logger
setupLogger();

// Observe loading of Roboto
const font = new FontFaceObserver('Roboto', {});

// When Roboto is loaded, add a font-family using Roboto to the body
font.load().then(() => {
  log.info('Roboto loaded');
  document.body.classList.add('fontLoaded');
});

const ROOT_CACHE_KEY = '__dsp_react_root__';

const getOrCreateRoot = mountNode => {
  if (!mountNode) return null;

  const cached = window[ROOT_CACHE_KEY];
  if (cached && cached.mountNode === mountNode && cached.root) {
    return cached.root;
  }

  const root = createRoot(mountNode);
  window[ROOT_CACHE_KEY] = { mountNode, root };
  return root;
};

const render = mountNode => {
  const root = getOrCreateRoot(mountNode);
  if (!root) return;
  const initialState = {};
  const store = configureStore(initialState);
  root.render(<ConnectedApp store={store} />);
};

// eslint-disable-next-line
const getUserDomain = () => {
  const userEmail = getUserEmail();
  return (userEmail && last(userEmail.split('@'))) || getUserIdp();
};

/**
 * When user is redirected to redirectUri after impersonate flow,
 * msal navigateToLoginRequestUrl will again redirect to /impersonate
 * Can't determine which flow to follow as msal is initializes at redirectUri, not at /impersonate
 * So checking here using getImpersonatedDisplayName and using redirect in react-router
 * @param {*} pathname
 * @returns bool
 */
const isImpersonateFlow = pathname =>
  pathname &&
  pathname.indexOf('impersonate') > -1 &&
  !getImpersonatedDisplayName();

/**
 * Convert to new URL format
 * @param {*} oldURL
 * @returns string
 */
const makeNewPAIURL = oldURL => {
  const decodedURL = decodeURIComponent(oldURL);
  const isEquipmentPage = decodedURL.includes('/equipment-details/pai');
  const url = new URL(decodedURL);
  const parts = url.pathname.split('/');
  const newParts = parts.map(part => part.split('_=_')[0]);
  if (isEquipmentPage) {
    const spliceIndex = newParts.length - 6;
    newParts.splice(spliceIndex, 1);
  } else if (newParts[newParts.length - 1] === 'list') {
    newParts[newParts.length - 1] = 'active';
  }
  url.pathname = newParts.join('/');
  return url.href;
};

// Get pathname
const urlObj = new URL(window.location.href);
const pathname = urlObj.pathname ? urlObj.pathname : null;

// Replace to new url, if old bookmark is used
if (/_=_(.*)?_=_/.test(decodeURIComponent(pathname))) {
  const newURL = makeNewPAIURL(window.location.href);
  window.location.replace(newURL);
}
// Go to impersonate flow based on url
else if (isImpersonateFlow(pathname)) {
  log.info('ImpersonateFlow');
  impersonateRedirect();
} else {
  loginUser()
    // eslint-disable-next-line no-unused-vars
    .then(async token => {
      const MOUNT_NODE = document.querySelector('#root');

      if (env.appInsightsConnectionString) {
        const { userId } = getLoggedUserInfo();
        if (userId) {
          appInsights.setAuthenticatedUserContext(userId, undefined, true);
        }
      }

      /* ReactDOM.render(
      <React.StrictMode>
        <Suspense fallback={<div>Loading...</div>}>
          <App />
        </Suspense>
      </React.StrictMode>,
      document.getElementById('root'),
    ); */

      render(MOUNT_NODE);

      /* if (module.hot) {
      // Hot reloadable translation json files and app
      // modules.hot.accept does not accept dynamic dependencies,
      // have to be constants at compile-time
      module.hot.accept(['./views', './locales/i18n'], () => {
        ReactDOM.unmountComponentAtNode(MOUNT_NODE);
        const App = require('./app').App;
        render(App);
      });
    } */
    })
    .catch(e => {
      log.error(e);
      if (isInteractionRequiredError(e)) {
        performAutoLogout('expired');
      }
    });
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
